import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Alert, Button, Card, Container, Form, InputGroup, Modal, Spinner, Table } from 'react-bootstrap'
import { Search } from 'lucide-react'
import './DistributorsSidebar.css'
import DistributorDetailSidebar from './DistributorDetailSidebar'
import {
  addDistributorContactAudit,
  createDistributorComparisonJobs,
  createDistributor,
  DistributorAiEnrichResult,
  DistributorDto,
  DistributorProductReportDto,
  enrichDistributorWithAi,
  fetchBusinessEntities,
  fetchDivisions,
  fetchOrphanedDistributorProducts,
  fetchProducts,
  fetchDistributorReport,
  fetchDistributors,
  fetchRulePacks,
  fetchUsers,
  mergePlaceholderProducts,
  ProductDto,
  RulePackDto,
  updateDistributor,
  upsertDistributorProductAudit,
  archiveDistributor,
  deleteDistributor,
  type DivisionDto,
} from '../services/api'

type DistributorDetailsDraft = {
  name: string
  address: string
  phone: string
  companyNumber: string
  marketingEmail: string
  primaryContactEmail: string
  secondaryContactEmail: string
  notes: string
  websiteUrl: string
  websiteCountry: string
  divisionId: string
  businessEntityId: string
  ownerAppUserId: string
  market: string
  approvalStatus: string
  organizationType: DistributorDto['organizationType']
}

type ContactAuditDraft = {
  contactedUtc: string
  reason: string
  contactMethod: string
  notes: string
}

const emptyDetailsDraft: DistributorDetailsDraft = {
  name: '',
  address: '',
  phone: '',
  companyNumber: '',
  marketingEmail: '',
  primaryContactEmail: '',
  secondaryContactEmail: '',
  notes: '',
  websiteUrl: '',
  websiteCountry: '',
  divisionId: '',
  businessEntityId: '',
  ownerAppUserId: '',
  market: '',
  approvalStatus: '',
  organizationType: 'Unclassified',
}

const emptyContactAuditDraft: ContactAuditDraft = {
  contactedUtc: '',
  reason: '',
  contactMethod: '',
  notes: '',
}

function toDateInput(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

function toDateTimeLocalInput(value?: string) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 16)
}

function toIsoFromDateInput(value: string) {
  if (!value) return undefined
  return new Date(`${value}T00:00:00`).toISOString()
}

function toIsoFromDateTimeLocalInput(value: string) {
  if (!value) return undefined
  return new Date(value).toISOString()
}

function mergeAiIntoDraft(draft: DistributorDetailsDraft, ai: DistributorAiEnrichResult): DistributorDetailsDraft {
  const pick = (current: string, suggested?: string) =>
    current.trim() ? current : (suggested?.trim() ?? current)

  return {
    ...draft,
    name: pick(draft.name, ai.name),
    address: pick(draft.address, ai.address),
    phone: pick(draft.phone, ai.phone),
    companyNumber: pick(draft.companyNumber, ai.companyNumber),
    marketingEmail: pick(draft.marketingEmail, ai.marketingEmail),
    primaryContactEmail: pick(draft.primaryContactEmail, ai.primaryContactEmail),
    secondaryContactEmail: pick(draft.secondaryContactEmail, ai.secondaryContactEmail),
    websiteCountry: pick(draft.websiteCountry, ai.websiteCountry),
    notes: pick(draft.notes, ai.notes),
  }
}

function normalizeWebsiteUrl(raw: string) {
  const trimmed = raw.trim()
  if (!trimmed) return { value: '', changed: false, valid: false }

  const withProtocol = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`

  try {
    const parsed = new URL(withProtocol)
    if (!parsed.hostname) {
      return { value: withProtocol, changed: withProtocol !== trimmed, valid: false }
    }
    return { value: parsed.toString(), changed: withProtocol !== trimmed || parsed.toString() !== trimmed, valid: true }
  } catch {
    return { value: withProtocol, changed: withProtocol !== trimmed, valid: false }
  }
}

function organizationTypeLabel(value: DistributorDto['organizationType']) {
  if (value === 'AccreditedDistributor') return 'Accredited distributor'
  if (value === 'IndependentReseller') return 'Independent reseller / third-party supplier'
  return 'Unclassified'
}

export default function Distributors() {
  const [searchParams] = useSearchParams()
  const addProductId = searchParams.get('addProduct')
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: distributors = [], isLoading } = useQuery({
    queryKey: ['distributors'],
    queryFn: fetchDistributors,
  })

  const { data: rulePacks = [] } = useQuery({
    queryKey: ['rulepacks', 'distributor-checks'],
    queryFn: fetchRulePacks,
  })

  const { data: businessEntities = [] } = useQuery({
    queryKey: ['business-entities', 'for-distributors'],
    queryFn: () => fetchBusinessEntities(),
  })

  const { data: divisions = [] } = useQuery<DivisionDto[]>({
    queryKey: ['divisions', 'for-distributors'],
    queryFn: fetchDivisions,
  })

  const { data: users = [] } = useQuery({
    queryKey: ['users', 'for-distributor-owner'],
    queryFn: fetchUsers,
  })

  const divisionNameById = new Map(divisions.map((d) => [d.id, d.name]))

  const headerSearch = searchParams.get('search')?.trim() ?? ''
  const [searchTerm, setSearchTerm] = useState(headerSearch)

  useEffect(() => {
    const query = searchParams.get('search')?.trim()
    if (query) setSearchTerm(query)
  }, [searchParams])
  const [newDistributorRootUrl, setNewDistributorRootUrl] = useState('')
  const [newDistributorCompanyName, setNewDistributorCompanyName] = useState('')
  const [newDistributorOrganizationType, setNewDistributorOrganizationType] = useState<DistributorDto['organizationType']>('AccreditedDistributor')
  const [newDistributorWebsiteCountry, setNewDistributorWebsiteCountry] = useState('')
  const [newDistributorDivisionId, setNewDistributorDivisionId] = useState('')
  const [newDistributorBusinessEntityId, setNewDistributorBusinessEntityId] = useState('')
  const [newDistributorOwnerAppUserId, setNewDistributorOwnerAppUserId] = useState('')
  const [newDistributorStatus, setNewDistributorStatus] = useState<string | null>(null)
  const [organizationTypeFilter, setOrganizationTypeFilter] = useState<'All' | DistributorDto['organizationType']>('All')

  const [selectedDistributor, setSelectedDistributor] = useState<DistributorDto | null>(null)
  const [detailsDraft, setDetailsDraft] = useState<DistributorDetailsDraft>(emptyDetailsDraft)
  const [detailsStatus, setDetailsStatus] = useState<string | null>(null)
  const [contactAuditDraft, setContactAuditDraft] = useState<ContactAuditDraft>(emptyContactAuditDraft)
  const [productDueDateByProductId, setProductDueDateByProductId] = useState<Record<string, string>>({})
  const [linkTarget, setLinkTarget] = useState<DistributorProductReportDto | null>(null)
  const [pimSearch, setPimSearch] = useState('')
  const [pimResults, setPimResults] = useState<ProductDto[]>([])
  const [pimSearchLoading, setPimSearchLoading] = useState(false)
  const [linkStatus, setLinkStatus] = useState<string | null>(null)
  const [aiEnrichStatus, setAiEnrichStatus] = useState<string | null>(null)
  const [distributorCheckRulePackId, setDistributorCheckRulePackId] = useState('')
  const [distributorCheckStatus, setDistributorCheckStatus] = useState<string | null>(null)
  const [latestDistributorCheckRunId, setLatestDistributorCheckRunId] = useState<string | null>(null)

  const { data: distributorReport, isFetching: isReportLoading } = useQuery({
    queryKey: ['distributor-report', selectedDistributor?.id],
    queryFn: () => fetchDistributorReport(selectedDistributor!.id),
    enabled: !!selectedDistributor?.id,
  })

  const { data: orphanedProducts = [] } = useQuery({
    queryKey: ['orphaned-distributor-products'],
    queryFn: fetchOrphanedDistributorProducts,
  })

  const filteredDistributors = distributors.filter((d) => {
    if (organizationTypeFilter !== 'All' && d.organizationType !== organizationTypeFilter) return false
    if (!searchTerm.trim()) return true
    const term = searchTerm.toLowerCase()
    return (
      (d.name ?? '').toLowerCase().includes(term) ||
      (d.domain ?? '').toLowerCase().includes(term) ||
      (d.websiteUrl ?? '').toLowerCase().includes(term) ||
      (d.businessEntityName ?? '').toLowerCase().includes(term)
    )
  })

  useEffect(() => {
    if (!selectedDistributor) {
      setDetailsDraft(emptyDetailsDraft)
      setAiEnrichStatus(null)
      setDistributorCheckStatus(null)
      setLatestDistributorCheckRunId(null)
      return
    }

    setAiEnrichStatus(null)
    setDetailsDraft({
      name: selectedDistributor.name ?? '',
      address: selectedDistributor.address ?? '',
      companyNumber: selectedDistributor.companyNumber ?? '',
      phone: selectedDistributor.phone ?? '',
      marketingEmail: selectedDistributor.marketingEmail ?? '',
      primaryContactEmail: selectedDistributor.primaryContactEmail ?? selectedDistributor.contactEmail ?? '',
      secondaryContactEmail: selectedDistributor.secondaryContactEmail ?? '',
      notes: selectedDistributor.notes ?? '',
      websiteUrl: selectedDistributor.websiteUrl ?? `https://${selectedDistributor.domain}/`,
      websiteCountry: selectedDistributor.websiteCountry ?? '',
      divisionId: selectedDistributor.divisionId ?? '',
      businessEntityId: selectedDistributor.businessEntityId ?? '',
      ownerAppUserId: selectedDistributor.ownerAppUserId ?? '',
      market: selectedDistributor.market ?? '',
      approvalStatus: selectedDistributor.approvalStatus ?? '',
      organizationType: selectedDistributor.organizationType ?? 'Unclassified',
    })
    setDetailsStatus(null)
    setContactAuditDraft((prev) => ({
      ...prev,
      contactedUtc: prev.contactedUtc || toDateTimeLocalInput(new Date().toISOString()),
    }))
  }, [selectedDistributor])

  useEffect(() => {
    if (distributorCheckRulePackId) return
    const approved = rulePacks.find((pack: RulePackDto) => pack.status === 'Approved')
    const fallback = approved ?? rulePacks[0]
    if (fallback) setDistributorCheckRulePackId(fallback.id)
  }, [distributorCheckRulePackId, rulePacks])

  useEffect(() => {
    const next: Record<string, string> = {}
    for (const p of distributorReport?.productsFound ?? []) {
      next[p.productId] = toDateInput(p.nextAuditDueUtc)
    }
    setProductDueDateByProductId(next)
  }, [distributorReport])

  const orphanedProductIds = new Set(orphanedProducts.map((row) => row.productId))

  const linkProductMutation = useMutation({
    mutationFn: ({ sourceProductId, destinationProductId }: { sourceProductId: string; destinationProductId: string }) =>
      mergePlaceholderProducts({
        productIds: [sourceProductId],
        destinationProductId,
      }),
    onSuccess: () => {
      setLinkStatus('Linked successfully. Refreshing distributor products...')
      qc.invalidateQueries({ queryKey: ['distributor-report', selectedDistributor?.id] })
      qc.invalidateQueries({ queryKey: ['orphaned-distributor-products'] })
      qc.invalidateQueries({ queryKey: ['products'] })
      setTimeout(() => {
        setLinkTarget(null)
        setPimSearch('')
        setPimResults([])
        setLinkStatus(null)
      }, 900)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? (typeof err?.response?.data === 'string' ? err.response.data : null) ?? err?.message ?? 'Failed to link product.'
      setLinkStatus(msg)
    },
  })

  const addDistributorMutation = useMutation({
    mutationFn: () =>
      createDistributor({
        rootUrl: normalizeWebsiteUrl(newDistributorRootUrl).value,
        companyName: newDistributorCompanyName.trim() || undefined,
        organizationType: newDistributorOrganizationType,
        websiteCountry: newDistributorWebsiteCountry.trim() || undefined,
        divisionId: newDistributorDivisionId || undefined,
        businessEntityId: newDistributorBusinessEntityId || undefined,
         ownerAppUserId: newDistributorOwnerAppUserId || undefined,
      }),
    onSuccess: (created) => {
      setNewDistributorStatus('Distributor created.')
      setNewDistributorRootUrl('')
      setNewDistributorCompanyName('')
      setNewDistributorOrganizationType('AccreditedDistributor')
      setNewDistributorWebsiteCountry('')
      setNewDistributorBusinessEntityId('')
       setNewDistributorDivisionId('')
       setNewDistributorOwnerAppUserId('')
      setSelectedDistributor(created)
      qc.invalidateQueries({ queryKey: ['distributors'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? (typeof err?.response?.data === 'string' ? err.response.data : null) ?? err?.message ?? 'Failed to create distributor.'
      setNewDistributorStatus(msg)
    },
  })

  const archiveDistributorMutation = useMutation({
    mutationFn: (id: string) => archiveDistributor(id, true),
    onSuccess: () => {
      setDetailsStatus('Distributor archived.')
      setSelectedDistributor(null)
      void qc.invalidateQueries({ queryKey: ['distributors'] })
    },
    onError: (err: Error) => setDetailsStatus(`Failed to archive: ${err.message}`),
  })

  const deleteDistributorMutation = useMutation({
    mutationFn: (id: string) => deleteDistributor(id),
    onSuccess: () => {
      setDetailsStatus('Distributor deleted.')
      setSelectedDistributor(null)
      void qc.invalidateQueries({ queryKey: ['distributors'] })
    },
    onError: (err: Error) => setDetailsStatus(`Failed to delete: ${err.message}`),
  })

  const updateDistributorMutation = useMutation({
    mutationFn: ({ id }: { id: string }) => {
      const normalizedWebsite = normalizeWebsiteUrl(detailsDraft.websiteUrl)
      return updateDistributor(id, {
        name: detailsDraft.name,
        address: detailsDraft.address,
        phone: detailsDraft.phone,
        companyNumber: detailsDraft.companyNumber,
        marketingEmail: detailsDraft.marketingEmail,
        primaryContactEmail: detailsDraft.primaryContactEmail,
        secondaryContactEmail: detailsDraft.secondaryContactEmail,
        notes: detailsDraft.notes,
        websiteUrl: normalizedWebsite.value,
        websiteCountry: detailsDraft.websiteCountry,
        divisionId: detailsDraft.divisionId || undefined,
        divisionIdCleared: !detailsDraft.divisionId,
        businessEntityId: detailsDraft.businessEntityId || undefined,
        businessEntityIdCleared: !detailsDraft.businessEntityId,
        ownerAppUserId: detailsDraft.ownerAppUserId || undefined,
        ownerAppUserIdCleared: !detailsDraft.ownerAppUserId,
        market: detailsDraft.market,
        approvalStatus: detailsDraft.approvalStatus,
        organizationType: detailsDraft.organizationType,
      })
    },
    onSuccess: (updated) => {
      setSelectedDistributor(updated)
      setDetailsStatus('Distributor details saved.')
      qc.invalidateQueries({ queryKey: ['distributors'] })
      qc.invalidateQueries({ queryKey: ['distributor-report', updated.id] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? (typeof err?.response?.data === 'string' ? err.response.data : null) ?? err?.message ?? 'Failed to save distributor details.'
      setDetailsStatus(msg)
    },
  })

  const addContactAuditMutation = useMutation({
    mutationFn: ({ id }: { id: string }) =>
      addDistributorContactAudit(id, {
        contactedUtc: toIsoFromDateTimeLocalInput(contactAuditDraft.contactedUtc),
        reason: contactAuditDraft.reason,
        contactMethod: contactAuditDraft.contactMethod || undefined,
        notes: contactAuditDraft.notes || undefined,
      }),
    onSuccess: () => {
      setContactAuditDraft({
        contactedUtc: toDateTimeLocalInput(new Date().toISOString()),
        reason: '',
        contactMethod: '',
        notes: '',
      })
      qc.invalidateQueries({ queryKey: ['distributor-report', selectedDistributor?.id] })
    },
  })

  const aiEnrichMutation = useMutation({
    mutationFn: () => enrichDistributorWithAi(selectedDistributor!.id),
    onSuccess: (result) => {
      setDetailsDraft((d) => mergeAiIntoDraft(d, result))
      setAiEnrichStatus(result.message ?? 'AI suggestions applied to empty fields. Review and save.')
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? err?.message ?? 'AI lookup failed.'
      setAiEnrichStatus(msg)
    },
  })

  const upsertProductAuditMutation = useMutation({
    mutationFn: ({ distributorId, productId }: { distributorId: string; productId: string }) =>
      upsertDistributorProductAudit(distributorId, productId, {
        nextAuditDueUtc: toIsoFromDateInput(productDueDateByProductId[productId] ?? ''),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['distributor-report', selectedDistributor?.id] })
    },
  })

  const distributorCheckMutation = useMutation({
    mutationFn: () => {
      if (!selectedDistributor) throw new Error('Select a distributor first.')
      return createDistributorComparisonJobs({
        distributorId: selectedDistributor.id,
        rulePackId: distributorCheckRulePackId,
        mode: 'OneOff',
        reason: 'Distributor-wide website compliance check',
        initiatedBy: 'ui-distributor-checks',
        reviewContext: { reviewMode: 'MultilingualReview' },
      })
    },
    onMutate: () => {
      setDistributorCheckStatus('Creating distributor-wide checks...')
      setLatestDistributorCheckRunId(null)
    },
    onSuccess: (result) => {
      const skipped = result.skippedProducts?.length ? ` ${result.skippedProducts.length} product(s) skipped.` : ''
      setDistributorCheckStatus(`${result.message} ${result.targetCount} URL target(s).${skipped}`)
      setLatestDistributorCheckRunId(result.createdRuns[0]?.checkRunId ?? null)
      qc.invalidateQueries({ queryKey: ['check-runs'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? (typeof err?.response?.data === 'string' ? err.response.data : null) ?? err?.message ?? 'Failed to create distributor-wide checks.'
      setDistributorCheckStatus(msg)
    },
  })

  const handleSaveDistributorDetails = () => {
    if (!selectedDistributor) return
    if (!detailsDraft.name.trim()) {
      setDetailsStatus('Company name is required.')
      return
    }
    const normalizedWebsite = normalizeWebsiteUrl(detailsDraft.websiteUrl)
    if (detailsDraft.websiteUrl.trim() && !normalizedWebsite.valid) {
      setDetailsStatus('Website URL is invalid. Use a full website address such as https://example.com.')
      return
    }
    if (detailsDraft.websiteUrl !== normalizedWebsite.value) {
      setDetailsDraft((d) => ({ ...d, websiteUrl: normalizedWebsite.value }))
    }
    updateDistributorMutation.mutate({ id: selectedDistributor.id })
  }

  const handleAddDistributor = () => {
    const normalized = normalizeWebsiteUrl(newDistributorRootUrl)
    if (!normalized.valid) {
      setNewDistributorStatus('Enter a valid website URL (for example https://example.com).')
      return
    }

    if (normalized.changed) {
      setNewDistributorRootUrl(normalized.value)
      setNewDistributorStatus('URL updated to include protocol automatically.')
    } else {
      setNewDistributorStatus(null)
    }

    addDistributorMutation.mutate()
  }

  const openLinkModal = (product: DistributorProductReportDto) => {
    setLinkTarget(product)
    setPimSearch('')
    setPimResults([])
    setLinkStatus(null)
  }

  const closeLinkModal = () => {
    if (linkProductMutation.isPending) return
    setLinkTarget(null)
    setPimSearch('')
    setPimResults([])
    setLinkStatus(null)
  }

  const handlePimSearch = async (term: string) => {
    setPimSearch(term)
    if (term.trim().length < 2) {
      setPimResults([])
      return
    }

    setPimSearchLoading(true)
    try {
      const results = await fetchProducts({ search: term.trim() })
      setPimResults(results.slice(0, 20))
    } catch {
      setPimResults([])
    } finally {
      setPimSearchLoading(false)
    }
  }

  const handleLinkToExistingProduct = (destinationProductId: string) => {
    if (!linkTarget) return
    setLinkStatus('Linking product...')
    linkProductMutation.mutate({
      sourceProductId: linkTarget.productId,
      destinationProductId,
    })
  }

  const handleCompareProduct = (productId: string) => {
    if (!selectedDistributor) return
    navigate(`/products/${productId}/compare?distributorId=${selectedDistributor.id}`)
  }

  const getOrphanedProductDetails = (productId: string) =>
    orphanedProducts.find((row) => row.productId === productId)

  return (
    <Container className="distributors-page py-2">
      <h2>Suppliers &amp; Distributors</h2>
      <p className="page-subtitle">Manage accredited distributors and independent third-party suppliers, their websites, contacts, and compliance checks.</p>

      {addProductId && (
        <Alert variant="info" className="mb-2 py-2 small">
          Add a product URL for this product: select a distributor below and run &quot;Discover Product Pages&quot; to find product links, or add targets manually via the Targets page. <Link to="/products">Back to Product library</Link>
        </Alert>
      )}

      <div className="mim-split distributors-split">
      <div className="mim-stack mim-split-sticky-list">
      <Card className="distributors-main-card">
        <Card.Body>
          <Form className="distributors-toolbar" onSubmit={(e) => { e.preventDefault(); handleAddDistributor() }} data-tour="distributors-add">
            <p className="distributors-toolbar-title">Add supplier or distributor</p>
            <div className="toolbar-field toolbar-field--url">
              <Form.Label>Root URL</Form.Label>
              <Form.Control
                type="url"
                size="sm"
                placeholder="https://example.com"
                value={newDistributorRootUrl}
                onChange={(e) => {
                  setNewDistributorRootUrl(e.target.value)
                  setNewDistributorStatus(null)
                }}
                onBlur={() => {
                  if (!newDistributorRootUrl.trim()) return
                  const normalized = normalizeWebsiteUrl(newDistributorRootUrl)
                  if (normalized.changed) {
                    setNewDistributorRootUrl(normalized.value)
                    setNewDistributorStatus('URL updated to include protocol automatically.')
                  } else if (!normalized.valid) {
                    setNewDistributorStatus('Enter a valid website URL (for example https://example.com).')
                  }
                }}
              />
            </div>
            <div className="toolbar-field">
              <Form.Label>Company name</Form.Label>
              <Form.Control size="sm" value={newDistributorCompanyName} onChange={(e) => setNewDistributorCompanyName(e.target.value)} />
            </div>
            <div className="toolbar-field">
              <Form.Label>Organisation type</Form.Label>
              <Form.Select size="sm" value={newDistributorOrganizationType} onChange={(e) => setNewDistributorOrganizationType(e.target.value as DistributorDto['organizationType'])}>
                <option value="AccreditedDistributor">Accredited distributor</option>
                <option value="IndependentReseller">Independent reseller / third-party supplier</option>
                <option value="Unclassified">Unclassified</option>
              </Form.Select>
            </div>
            <div className="toolbar-field">
              <Form.Label>Country</Form.Label>
              <Form.Control size="sm" placeholder="GB" value={newDistributorWebsiteCountry} onChange={(e) => setNewDistributorWebsiteCountry(e.target.value)} />
            </div>
            <div className="toolbar-field">
              <Form.Label>Division</Form.Label>
              <Form.Select size="sm" value={newDistributorDivisionId} onChange={(e) => setNewDistributorDivisionId(e.target.value)}>
                <option value="">Select...</option>
                {divisions.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="toolbar-field">
              <Form.Label>Kingspan business</Form.Label>
              <Form.Select size="sm" value={newDistributorBusinessEntityId} onChange={(e) => setNewDistributorBusinessEntityId(e.target.value)}>
                <option value="">Unassigned</option>
                {businessEntities.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </Form.Select>
            </div>
            <div className="toolbar-field">
              <Form.Label>Owner</Form.Label>
              <Form.Select size="sm" value={newDistributorOwnerAppUserId} onChange={(e) => setNewDistributorOwnerAppUserId(e.target.value)}>
                <option value="">Unassigned</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.displayName || u.email || u.userId}</option>
                ))}
              </Form.Select>
            </div>
            <div className="toolbar-field toolbar-field--action">
              <Form.Label className="invisible">Add</Form.Label>
              <Button size="sm" type="submit" disabled={!newDistributorRootUrl.trim() || addDistributorMutation.isPending}>
                {addDistributorMutation.isPending ? 'Adding...' : 'Add'}
              </Button>
            </div>
          </Form>

          {newDistributorStatus && (
            <div className={`small mb-2 ${newDistributorStatus.startsWith('Failed') || newDistributorStatus.includes('valid') ? 'text-danger' : 'text-success'}`}>
              {newDistributorStatus}
            </div>
          )}

          <div className="distributors-table-toolbar">
            <div className="search-field">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="search"
                size="sm"
                placeholder="Name, domain, URL, or business"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="search-field">
              <Form.Label>Organisation type</Form.Label>
              <Form.Select size="sm" value={organizationTypeFilter} onChange={(e) => setOrganizationTypeFilter(e.target.value as typeof organizationTypeFilter)}>
                <option value="All">All types</option>
                <option value="AccreditedDistributor">Accredited distributors</option>
                <option value="IndependentReseller">Independent resellers / third-party suppliers</option>
                <option value="Unclassified">Unclassified</option>
              </Form.Select>
            </div>
            <span className="table-meta">
              {isLoading ? 'Loading…' : `${filteredDistributors.length} of ${distributors.length}`}
            </span>
          </div>

          {isLoading && <div className="small text-muted py-2">Loading distributors…</div>}
          {!isLoading && filteredDistributors.length === 0 && (
            <div className="small text-muted py-2">No distributors match your search.</div>
          )}
          {!isLoading && filteredDistributors.length > 0 && (
            <Table striped hover responsive size="sm" className="distributors-table" data-tour="distributors-list">
              <thead>
                <tr>
                  <th>Company name</th>
                  <th>Organisation type</th>
                  <th>Website / country</th>
                  <th>Division</th>
                  <th>Kingspan business</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDistributors.map((d: DistributorDto) => (
                  <tr
                    key={d.id}
                    className={selectedDistributor?.id === d.id ? 'is-selected' : undefined}
                    onClick={() => setSelectedDistributor(d)}
                  >
                    <td className="fw-medium">{d.name}</td>
                    <td>{organizationTypeLabel(d.organizationType)}</td>
                    <td>
                      <span className="text-truncate d-inline-block" style={{ maxWidth: 220 }}>
                        {d.websiteUrl ?? `https://${d.domain}/`}
                      </span>
                      <span className="text-muted ms-1">({d.websiteCountry ?? '—'})</span>
                    </td>
                    <td>{d.divisionId ? (divisionNameById.get(d.divisionId) ?? d.divisionId) : '—'}</td>
                    <td>{d.businessEntityName ?? '—'}</td>
                    <td className="text-end">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={(e) => { e.stopPropagation(); navigate(`/distributors/${d.id}/search`) }}
                      >
                        Discover pages
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      </div>
      <div className="mim-split-sticky-detail">
        {selectedDistributor ? (
          <DistributorDetailSidebar
            distributor={selectedDistributor}
            detailsDraft={detailsDraft}
            onDetailsChange={setDetailsDraft}
            detailsStatus={detailsStatus}
            aiEnrichStatus={aiEnrichStatus}
            contactAuditDraft={contactAuditDraft}
            onContactAuditChange={setContactAuditDraft}
            divisions={divisions}
            businessEntities={businessEntities}
            users={users}
            distributorReport={distributorReport}
            isReportLoading={isReportLoading}
            productDueDateByProductId={productDueDateByProductId}
            onProductDueDateChange={(productId, value) => setProductDueDateByProductId((all) => ({ ...all, [productId]: value }))}
            orphanedProductIds={orphanedProductIds}
            onClose={() => setSelectedDistributor(null)}
            onSaveDetails={handleSaveDistributorDetails}
            onAiEnrich={() => aiEnrichMutation.mutate()}
            onLogContact={() => addContactAuditMutation.mutate({ id: selectedDistributor.id })}
            onCompareProduct={handleCompareProduct}
            onLinkProduct={openLinkModal}
            onSaveProductDueDate={(productId) => upsertProductAuditMutation.mutate({ distributorId: selectedDistributor.id, productId })}
            isSavingDetails={updateDistributorMutation.isPending}
            isAiEnriching={aiEnrichMutation.isPending}
            isLoggingContact={addContactAuditMutation.isPending}
            isSavingProductAudit={upsertProductAuditMutation.isPending}
            rulePacks={rulePacks}
            distributorCheckRulePackId={distributorCheckRulePackId}
            onDistributorCheckRulePackChange={setDistributorCheckRulePackId}
            onRunDistributorCheck={() => distributorCheckMutation.mutate()}
            isRunningDistributorCheck={distributorCheckMutation.isPending}
            distributorCheckStatus={distributorCheckStatus}
            latestDistributorCheckRunId={latestDistributorCheckRunId}
            embedded
            onArchive={() => {
              if (!window.confirm(`Archive ${selectedDistributor.name}?`)) return
              archiveDistributorMutation.mutate(selectedDistributor.id)
            }}
            onDelete={() => {
              if (!window.confirm(`Delete ${selectedDistributor.name}? This cannot be undone.`)) return
              deleteDistributorMutation.mutate(selectedDistributor.id)
            }}
            isArchiving={archiveDistributorMutation.isPending}
            isDeleting={deleteDistributorMutation.isPending}
            onWebsiteUrlBlur={() => {
              if (!detailsDraft.websiteUrl.trim()) return
              const normalized = normalizeWebsiteUrl(detailsDraft.websiteUrl)
              if (normalized.changed) {
                setDetailsDraft((d) => ({ ...d, websiteUrl: normalized.value }))
                setDetailsStatus('Website URL updated to include protocol automatically.')
              } else if (!normalized.valid) {
                setDetailsStatus('Website URL is invalid. Use a full website address such as https://example.com.')
              }
            }}
          />
        ) : (
          <Card>
            <Card.Body className="text-muted small py-5 text-center">
              Select a supplier or distributor from the list to view details.
            </Card.Body>
          </Card>
        )}
      </div>
      </div>

      <Modal show={!!linkTarget} onHide={closeLinkModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Link distributor product</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {linkTarget && (
            <>
              <div className="bg-light rounded p-3 mb-3 small">
                <div className="fw-semibold">{linkTarget.productName}</div>
                <div className="text-muted" style={{ fontFamily: 'monospace' }}>{linkTarget.productCode}</div>
                {getOrphanedProductDetails(linkTarget.productId)?.distributorNames?.length ? (
                  <div className="text-muted mt-1">
                    Found on: {getOrphanedProductDetails(linkTarget.productId)?.distributorNames.join(', ')}
                  </div>
                ) : null}
                {linkTarget.checkedUrls[0] && (
                  <a href={linkTarget.checkedUrls[0]} target="_blank" rel="noreferrer" className="d-block mt-1 text-truncate">
                    {linkTarget.checkedUrls[0]}
                  </a>
                )}
              </div>

              <div className="small text-muted mb-2">
                Search the Kingspan product library, then link this distributor product to the correct canonical product.
              </div>

              <InputGroup className="mb-2" size="sm">
                <InputGroup.Text>
                  <Search size={14} />
                </InputGroup.Text>
                <Form.Control
                  placeholder="Search by Kingspan product code or name..."
                  value={pimSearch}
                  onChange={(e) => handlePimSearch(e.target.value)}
                  autoFocus
                />
                {pimSearchLoading && (
                  <InputGroup.Text>
                    <Spinner animation="border" size="sm" />
                  </InputGroup.Text>
                )}
              </InputGroup>

              {pimSearch.trim().length >= 2 && pimResults.length === 0 && !pimSearchLoading && (
                <div className="text-muted small text-center py-2">
                  No Kingspan products found matching "{pimSearch}".
                </div>
              )}

              {pimResults.length > 0 && (
                <div className="border rounded" style={{ maxHeight: 320, overflowY: 'auto' }}>
                  {pimResults.map((product) => (
                    <div
                      key={product.id}
                      className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom"
                      style={{ gap: '1rem' }}
                    >
                      <div className="flex-grow-1 min-width-0">
                        <div className="fw-semibold small">{product.name}</div>
                        <div className="text-muted" style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{product.code}</div>
                        {(product.market || product.family) && (
                          <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {[product.market, product.family].filter(Boolean).join(' · ')}
                          </div>
                        )}
                        {product.kingspanUrl && (
                          <a href={product.kingspanUrl} target="_blank" rel="noreferrer" className="text-muted" style={{ fontSize: '0.72rem' }}>
                            {product.kingspanUrl}
                          </a>
                        )}
                      </div>
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={linkProductMutation.isPending}
                        onClick={() => handleLinkToExistingProduct(product.id)}
                      >
                        Link this product
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {linkStatus && (
                <Alert
                  variant={linkStatus.toLowerCase().includes('failed') ? 'danger' : 'info'}
                  className="mt-3 mb-0 small py-2"
                >
                  {linkProductMutation.isPending && <Spinner animation="border" size="sm" className="me-2" />}
                  {linkStatus}
                </Alert>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={closeLinkModal} disabled={linkProductMutation.isPending}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
